import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useSearchParams } from 'react-router'
import { useAuth0 } from '@auth0/auth0-react'
import { FT_ADMIN_CLAIM } from 'common/c'
import { impersonateTenant } from 'api/user'
import type { AppState } from 'state/index'
import {
  getLogLinkAction,
  getTenantIdFromToken,
  parseLogLink,
  removeLogLinkParams,
  type LogLinkType,
} from './log-link'

const SWITCH_ATTEMPT_KEY = 'logLinkTenantSwitch'

export default function useLogLinkRequest(type: LogLinkType) {
  const [searchParams, setSearchParams] = useSearchParams()
  const permissions = useSelector(
    (state: AppState) => state.account.permissions,
  )
  const customClaims = useSelector(
    (state: AppState) => state.account.customClaims,
  )
  const token = useSelector((state: AppState) => state.account.token)
  const { getAccessTokenSilently } = useAuth0()
  const switchStartedRef = useRef(false)

  const isAdmin =
    permissions.includes('farmtrx:admin') || !!customClaims?.[FT_ADMIN_CLAIM]
  const currentTenantId = getTenantIdFromToken(token)
  const link = useMemo(
    () => parseLogLink(searchParams, type),
    [searchParams, type],
  )
  const action = getLogLinkAction({
    link,
    isAdmin,
    currentTenantId,
    switchAlreadyAttempted:
      !!link && sessionStorage.getItem(SWITCH_ATTEMPT_KEY) === link.tenantId,
  })

  useEffect(() => {
    if (action === 'open') sessionStorage.removeItem(SWITCH_ATTEMPT_KEY)
    if (action !== 'switch' || !link || switchStartedRef.current) return
    switchStartedRef.current = true
    sessionStorage.setItem(SWITCH_ATTEMPT_KEY, link.tenantId)
    impersonateTenant(
      link.tenantId,
      `${window.location.pathname}${window.location.search}`,
      () => getAccessTokenSilently(),
    ).catch((error) => {
      sessionStorage.removeItem(SWITCH_ATTEMPT_KEY)
      switchStartedRef.current = false
      console.error(error)
    })
  }, [action, link, getAccessTokenSilently])

  const clearLink = useCallback(() => {
    setSearchParams((params) => removeLogLinkParams(params), { replace: true })
  }, [setSearchParams])

  return {
    link: action === 'open' ? link : null,
    tenantId: isAdmin ? currentTenantId : undefined,
    clearLink,
  }
}
